import QuestionType from 'types/QuestionType';
import QuestionTypeRaw from 'types/QuestionTypeRaw';

import CheckboxQuestion from './CheckboxQuestion/CheckboxQuestion';
import SelectionQuestion from './SelectionQuestion/SelectionQuestion';
import TextAreaQuestion from './TextAreaQuestion/TextAreaQuestion';
import TextQuestion from './TextQuestion/TextQuestion';
import UrlQuestion from './UrlQuestion/UrlQuestion';
import KliparoQuestion from '../../types/api/KliparoQuestion';
import { TFunction } from 'i18next';
import * as yup from 'yup';
import { UnprivilegedEditor } from 'react-quill';
import { TestContext } from 'yup';
import OrganizationQuestionDto from '../../types/api/OrganizationQuestionDto';
import { FormikRichTextAreaValue } from '../forms/formik/FormikRichTextAreaInput/FormikRichTextAreaInput';
import traroMarkdown from '../Markdown/plugins/traroMarkdown';

export type QuestionFormValues =
  | { id: number | null; type: QuestionType.TEXT | QuestionTypeRaw.TEXT; value: string }
  | { id: number | null; type: QuestionType.LONG_TEXT | QuestionTypeRaw.LONG_TEXT; value: FormikRichTextAreaValue }
  | { id: number | null; type: QuestionType.URL | QuestionTypeRaw.URL; value: string }
  | { id: number | null; type: QuestionType.DISPLAY_ONLY | QuestionTypeRaw.DISPLAY_ONLY; value: string }
  | { id: number | null; type: QuestionType.CHECKBOX | QuestionTypeRaw.CHECKBOX; value: Record<string, boolean> }
  | {
      id: number | null;
      type: QuestionType.SELECTION | QuestionTypeRaw.SELECTION;
      value: { value: string; label: string } | null;
    };

const getComponent = (questionType: QuestionType | QuestionTypeRaw) => {
  switch (questionType) {
    case QuestionType.TEXT:
    case QuestionTypeRaw.TEXT:
      return TextQuestion;
    case QuestionType.LONG_TEXT:
    case QuestionTypeRaw.LONG_TEXT:
      return TextAreaQuestion;
    case QuestionType.CHECKBOX:
    case QuestionTypeRaw.CHECKBOX:
      return CheckboxQuestion;
    case QuestionType.SELECTION:
    case QuestionTypeRaw.SELECTION:
      return SelectionQuestion;
    case QuestionType.URL:
    case QuestionTypeRaw.URL:
      return UrlQuestion;
    case QuestionType.DISPLAY_ONLY:
    case QuestionTypeRaw.DISPLAY_ONLY:
      return () => null;
    default:
      throw new Error('Invalid question type: ' + questionType);
  }
};

function isKliparoQuestion(obj: KliparoQuestion | OrganizationQuestionDto): obj is KliparoQuestion {
  return (obj as KliparoQuestion).question_type !== undefined;
}

function isOrganizationQuestion(obj: KliparoQuestion | OrganizationQuestionDto): obj is OrganizationQuestionDto {
  return (obj as OrganizationQuestionDto).type !== undefined;
}

const getQuestionType = (question: KliparoQuestion | OrganizationQuestionDto): QuestionTypeRaw | QuestionType => {
  if (isKliparoQuestion(question)) {
    return question.question_type;
  }

  if (isOrganizationQuestion(question)) {
    return question.type;
  }

  throw new Error('Invalid object provided!');
};

const getQuestionValue = (question: KliparoQuestion | OrganizationQuestionDto): string => {
  if (isKliparoQuestion(question)) {
    return question.question_values;
  }

  if (isOrganizationQuestion(question)) {
    return question.values;
  }

  throw new Error('Invalid object provided!');
};

const getQuestionText = (question: KliparoQuestion | OrganizationQuestionDto): string => {
  if (isKliparoQuestion(question)) {
    return traroMarkdown.tags.currentDateTime.replace(question.question_text);
  }

  if (isOrganizationQuestion(question)) {
    return traroMarkdown.tags.currentDateTime.replace(question.question);
  }

  throw new Error('Invalid object provided!');
};

export const getDefaultInitialValue = (question: KliparoQuestion | OrganizationQuestionDto): QuestionFormValues => {
  const questionType = getQuestionType(question);
  const questionValue = getQuestionValue(question);

  switch (questionType) {
    case QuestionType.TEXT:
    case QuestionTypeRaw.TEXT:
      return {
        id: question.id,
        type: questionType,
        value: '',
      };
    case QuestionType.LONG_TEXT:
    case QuestionTypeRaw.LONG_TEXT:
      return {
        id: question.id,
        type: questionType,
        value: {
          value: '',
          editor: null,
        },
      };
    case QuestionType.CHECKBOX:
    case QuestionTypeRaw.CHECKBOX:
      return {
        id: question.id,
        type: questionType,
        value: questionValue.split(';').reduce((acc, elementValue) => {
          acc[window.btoa(elementValue)] = false;

          return acc;
        }, {} as Record<string, boolean>),
      };
    case QuestionType.SELECTION:
    case QuestionTypeRaw.SELECTION:
      return {
        id: question.id,
        type: questionType,
        value: null,
      };
    case QuestionType.URL:
    case QuestionTypeRaw.URL:
      return {
        id: question.id,
        type: questionType,
        value: '',
      };
    case QuestionType.DISPLAY_ONLY:
    case QuestionTypeRaw.DISPLAY_ONLY:
      return {
        id: question.id,
        type: questionType,
        value: getQuestionText(question),
      };

    default:
      throw new Error('Invalid question type: ' + questionType);
  }
};

const getTextQuestionSchema = (question: KliparoQuestion | OrganizationQuestionDto, t: TFunction) => {
  let schema = yup
    .string()
    .min(1, t('errors.minLength', { count: 1 }))
    .required(t('errors.minLength', { count: 1 }));

  const maxCharacters = Number(getQuestionValue(question));
  if (maxCharacters > 0) {
    schema = schema.max(maxCharacters, t('errors.maxLength', { count: maxCharacters }));
  }

  return schema;
};

const getLongTextQuestionSchema = (question: KliparoQuestion | OrganizationQuestionDto, t: TFunction) => {
  const maxCharacters = Number(getQuestionValue(question));

  return yup.object().shape({
    editor: yup.mixed().test((editor: UnprivilegedEditor | null, context: TestContext) => {
      if (!context.parent.value) {
        return context.createError({ message: t('errors.minLength', { count: 1 }) });
      }

      if (!editor) {
        return true;
      }

      const textLength = editor.getText().trim().length;
      if (textLength === 0) {
        return context.createError({ message: t('errors.minLength', { count: 1 }) });
      }

      if (textLength > maxCharacters && maxCharacters > 0) {
        return context.createError({ message: t('errors.maxLength', { count: maxCharacters }) });
      }

      return true;
    }),
  });
};

export const getQuestionSchema = (question: KliparoQuestion | OrganizationQuestionDto, t: TFunction) => {
  const questionType = getQuestionType(question);

  switch (questionType) {
    case QuestionType.TEXT:
    case QuestionTypeRaw.TEXT:
      return getTextQuestionSchema(question, t);
    case QuestionType.LONG_TEXT:
    case QuestionTypeRaw.LONG_TEXT:
      return getLongTextQuestionSchema(question, t);
    case QuestionType.URL:
    case QuestionTypeRaw.URL:
      return yup
        .string()
        .min(1, t('errors.minLength', { count: 1 }))
        .when({
          is: 'N/A',
          then: yup.string().nullable(),
          otherwise: yup
            .string()
            .matches(/^(((http)s?\:)?\/\/)?([a-z0-9][\w\.\-]+\.)+([a-zA-Z0-9]){2,}(\/.*)?$/, t('errors.urlOrNa'))
            .required(t('errors.minLength', { count: 1 })),
        });
    case QuestionType.CHECKBOX:
    case QuestionTypeRaw.CHECKBOX:
      if (isKliparoQuestion(question) || !question.required) {
        return yup.object();
      } else {
        return yup.object().test((value, context) => {
          if (Object.keys(value).some((key) => value[key])) {
            return true;
          }

          return context.createError({
            path: context.path + '.' + Object.keys(value)[0],
            message: t('errors.required'),
          });
        });
      }
    case QuestionType.SELECTION:
    case QuestionTypeRaw.SELECTION:
      return yup.object().nullable().required(t('errors.required'));
    case QuestionType.DISPLAY_ONLY:
    case QuestionTypeRaw.DISPLAY_ONLY:
      return yup.mixed().nullable();
    default:
      throw new Error('Invalid question type: ' + questionType);
  }
};

export default getComponent;
